import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { Box } from '../../components/Box'
import { CenterChildren } from '../../components/Center'
import { themeColor } from '../../components/theme'
import { DelayRender } from '../../components/DelayRender'
import { Flex } from '../../components/Flex'
import { Spin, SpinSize } from '../../components/Spin'
import { Text, Title } from '../../components/Text'

import { PriceVisibility } from '../PriceVisibility'
import { TaxesFootnote } from '../TaxesFootnote'
import { useApiData } from '../../hooks/useApiData'
import { useAccountingStatus } from '../../hooks/useAccountingStatus'
import {
  getTotalCheckoutNetValueFormatted,
  getTotalCheckoutTaxValueFormatted,
  getTotalCheckoutValueWithTaxFormatted,
  isAccountingError,
  isCheckoutLoading
} from '../../redux/checkout'
import { getPricesMode, PricesMode } from '../../redux/config'
import { getDomains } from '../../redux/domains/selectors'
import { RootState } from '../../redux/types'
import { ContractType } from '../../redux/user'
import { OrderItem } from '../../redux/order'

import { SidebarCartItem } from './SidebarCartItem'
import { ReportDetailRes, StyledFieldProps, requiredDomains } from './types'
import { DocumentIcon } from './styled'
import { SideBannerProps as BaseSideBannerProps } from './SideBannerProps'

type SideBannerPros = BaseSideBannerProps & {
  checkoutItems: OrderItem[]
  isCustomerRequestErrorInCheckout: boolean | undefined
  ItemWrapperComponent: React.ComponentType
}

const CartField = ({ label, value }: StyledFieldProps) => (
  <Flex pt="10px" flexDirection="row">
    <Title level="small" bold padding="0px 12px 0 0">
      {label}
      {!_.isEmpty(value) ? ':' : ''}
    </Title>
    {!_.isEmpty(value) && <span>{value}</span>}
  </Flex>
)

const OrderErrorWarning = styled(Box)`
  color: ${themeColor('danger')};
  font-weight: bold;
`

export const SideBanner = ({
  checkoutItems,
  isCustomerRequestErrorInCheckout,
  contractType,
  ItemWrapperComponent,
  displayProductDescription = false
}: SideBannerPros) => {
  const { t, i18n } = useTranslation(['cart', 'checkout'])
  const totalPrice = useSelector(getTotalCheckoutNetValueFormatted)
  const orderLoading = useSelector(isCheckoutLoading)
  const totalPriceWithTax = useSelector(getTotalCheckoutValueWithTaxFormatted)
  const totalTaxValue = useSelector(getTotalCheckoutTaxValueFormatted)

  const productCodes = _.uniq(_.map(checkoutItems, x => x.reportType))
  const pricesMode = useSelector(getPricesMode)
  const accountingError = useSelector(isAccountingError)

  const { data: productsUmbracoData, loading: productsLoading } = useApiData<ReportDetailRes>(
    {
      url: '/api/products/products',
      method: 'post',
      data: { productCodes }
    },
    {
      umbracoCall: true,
      skip: _.isEmpty(productCodes)
    }
  )
  const domainsData = useSelector((state: RootState) =>
    getDomains(state, {
      domains: requiredDomains,
      language: i18n.language
    })
  )

  const loading = orderLoading || productsLoading

  const isSapOnline = useAccountingStatus()

  return (
    <Flex flexDirection="column" m="20px 12px" width={[1, 0.6]}>
      <Box height="fit-content" p={2} border="1px solid" borderColor="borderPrimary" borderRadius="4px">
        {loading && (
          <DelayRender delay={300}>
            <CenterChildren>
              <Spin size={SpinSize.ExtraLarge} />
            </CenterChildren>
          </DelayRender>
        )}
        {!loading && accountingError && (
          <Flex flexDirection="column">
            <Title level="medium" color="danger">
              {t('cart:AccountingErrorTitle')}
            </Title>
            <Text py={2}>{t('cart:AccountingErrorText')}</Text>
          </Flex>
        )}
        {!loading && !accountingError && !_.isEmpty(checkoutItems) && (
          <>
            <Flex>
              <DocumentIcon size="large" />
              <Text color="primary" fontSize="16px" pl="12px" bold>
                {`${checkoutItems.length} ${t('checkout:ItemCount')}`}
              </Text>
            </Flex>
            <ItemWrapperComponent>
              {_.map(checkoutItems, item => {
                const umbracoData = _.find(productsUmbracoData?.data, y => y.bifCode === item.reportType)
                return (
                  <Flex flexDirection="column" key={item.orderId}>
                    <SidebarCartItem
                      data={item}
                      domainsData={domainsData}
                      umbracoData={umbracoData}
                      displayProductDescription={displayProductDescription}
                    />
                    <PriceVisibility>
                      <Box width="100%" height="1px" backgroundColor="borderPrimary" />
                    </PriceVisibility>
                  </Flex>
                )
              })}
            </ItemWrapperComponent>
            {pricesMode == PricesMode.WithTaxInfo && contractType === ContractType.PayperClick && (
              <PriceVisibility>
                <CartField
                  label={t('checkout:TotalTaxValue')}
                  value={
                    <Text color="secondary" fontSize="16px" bold>
                      {totalTaxValue}
                    </Text>
                  }
                />
              </PriceVisibility>
            )}
            <PriceVisibility>
              <Flex flexWrap="wrap" pt="20px">
                <Text color="primary" fontSize="24px" pr="16px" bold>
                  {t('checkout:TotalPrice')}:
                </Text>
                <Text color="secondary" fontSize="24px" bold>
                  {pricesMode === PricesMode.OnlyGross || contractType === ContractType.PayperClick
                    ? totalPriceWithTax
                    : totalPrice}
                </Text>
              </Flex>
            </PriceVisibility>
          </>
        )}
      </Box>
      {!accountingError && <TaxesFootnote wrapperProps={{ m: 1 }} />}
      {pricesMode === PricesMode.WithTaxInfo && contractType === ContractType.Prepaid && (
        <Text m={1} color="textMuted">
          {t('checkout:PrepaidTotalsNote')}
        </Text>
      )}
      {!accountingError && isCustomerRequestErrorInCheckout && (
        <Text m={1} color="danger">
          {t('checkout:ErrorInCheckoutItems')}
        </Text>
      )}
      {!_.isEmpty(checkoutItems) &&
        !isSapOnline.loading &&
        isSapOnline.isOffline &&
        isSapOnline.disclaimers?.visibility?.showInCheckout && (
          <OrderErrorWarning m="1">{'*' + t('cart:ServiceUnavailable')}</OrderErrorWarning>
        )}
    </Flex>
  )
}
